import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Users, Truck, X } from "lucide-react";
import { DocumentResults } from "@/components/GlobalSearchDocuments";
import { searchAPI } from "@/lib/api";

export default function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);
  const timerRef = useRef(null);

  const search = useCallback((q) => {
    if (q.length < 2) { setResults(null); return; }
    setLoading(true);
    searchAPI.query(q)
      .then((res) => setResults(res.data))
      .catch(() => setResults(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (query.length >= 2) {
      timerRef.current = setTimeout(() => search(query), 300);
    } else {
      setResults(null);
    }
    return () => clearTimeout(timerRef.current);
  }, [query, search]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const go = (path) => {
    setOpen(false);
    setQuery("");
    setResults(null);
    navigate(path);
  };

  const hasResults = results && (results.clients?.length > 0 || results.suppliers?.length > 0 || results.documents?.length > 0);
  const noResults = results && !hasResults;

  return (
    <div ref={ref} className="relative w-full" data-testid="global-search">
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Clients, fournisseurs, n° de document..."
          className="w-full pl-9 pr-8 py-2 text-sm bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
          data-testid="global-search-input"
        />
        {query && (
          <button onClick={() => { setQuery(""); setResults(null); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300" data-testid="global-search-clear">
            <X size={14} />
          </button>
        )}
      </div>
      {open && query.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-slate-800 border border-slate-700 rounded-lg shadow-xl overflow-hidden z-50 max-h-80 overflow-y-auto" data-testid="global-search-results">
          {loading && (
            <div className="px-4 py-3 text-xs text-slate-400 text-center">Recherche...</div>
          )}
          {!loading && noResults && (
            <div className="px-4 py-3 text-xs text-slate-400 text-center">Aucun résultat pour "{query}"</div>
          )}
          {!loading && hasResults && (
            <>
              {results.clients?.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider bg-slate-900/50">Clients</div>
                  {results.clients.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => go(`/clients/${c.id}`)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-700/50 transition-colors text-left"
                      data-testid={`search-result-client-${c.id}`}
                    >
                      <div className="w-7 h-7 rounded-md bg-indigo-600/20 flex items-center justify-center shrink-0">
                        <Users size={13} className="text-indigo-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{c.name}</p>
                        <p className="text-[10px] text-slate-500 truncate">
                          {c.type === "prospect" ? "Prospect" : "Client"}
                          {c.city ? ` - ${c.city}` : ""}
                          {c.phone ? ` - ${c.phone}` : ""}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {results.suppliers?.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider bg-slate-900/50">Fournisseurs</div>
                  {results.suppliers.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => go(`/fournisseurs/${s.id}`)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-700/50 transition-colors text-left"
                      data-testid={`search-result-supplier-${s.id}`}
                    >
                      <div className="w-7 h-7 rounded-md bg-blue-600/20 flex items-center justify-center shrink-0">
                        <Truck size={13} className="text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{s.name}</p>
                        <p className="text-[10px] text-slate-500 truncate">
                          {s.contact ? s.contact : ""}
                          {s.city ? ` - ${s.city}` : ""}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <DocumentResults documents={results.documents} onOpen={(d) => go(`/documents?open=${d.id}`)} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
